import React, { CSSProperties, useMemo } from 'react'

import AppMarker, { AppMarkerProps } from 'components/AppMarker'
import SvgWaterDrop from 'components/SvgWaterDrop'
import { Maybe, ServiceLineMaterial } from 'generated/graphql'
import useAccount from 'hooks/useAccount'
import useSvgDataUri from 'hooks/useSvgDataUri'
import {
  isLead,
  isUnknown,
  serviceLineMaterialIcon,
  serviceLineMaterialColor,
  isGRR
} from 'utils/serviceLineMaterialUtils'

export interface AppMarkerServiceLineProps extends AppMarkerProps {
  assetId: string
  assetOpen: string
  locationId: number
  privateMaterial?: Maybe<ServiceLineMaterial>
  privateVerifiedDate?: Maybe<string>
  publicMaterial?: Maybe<ServiceLineMaterial>
  publicVerifiedDate?: Maybe<string>
  setAssetOpen: (value: string) => void
}

const AppMarkerServiceLine = ({
  assetId,
  assetOpen,
  locationId,
  privateMaterial,
  privateVerifiedDate,
  publicMaterial,
  publicVerifiedDate,
  setAssetOpen,
  ...rest
}: AppMarkerServiceLineProps) => {
  const { ref, uri } = useSvgDataUri()
  const { hideServiceLines } = useAccount()

  const size = 48

  const icon = useMemo<undefined | google.maps.Icon>(() => {
    if (!uri) {
      return
    }

    return {
      anchor: new google.maps.Point(size / 2, size / 4),
      scaledSize: {
        height: size / 2,
        width: size,
        equals: other => other.width === other.height
      },
      url: uri
    }
  }, [uri])

  const publicDisplay = publicMaterial?.epaReportingCategory
  const privateDisplay = privateMaterial?.epaReportingCategory

  const publicMaterialIcon = serviceLineMaterialIcon(publicDisplay)
  const privateMaterialIcon = serviceLineMaterialIcon(privateDisplay)

  const publicMaterialColor = serviceLineMaterialColor(publicDisplay)
  const privateMaterialColor = serviceLineMaterialColor(privateDisplay)

  const iconOffset = (material?: Maybe<string>): number => {
    if (!material) return 0
    if (isLead(material)) return 400
    if (isGRR(material)) return 400
    if (isUnknown(material)) return 300
    return 220
  }

  const RIGHT_SIDE_OFFSET = 1020

  const publicIconOffset = iconOffset(publicDisplay)
  const privateIconOffset = iconOffset(privateDisplay) + RIGHT_SIDE_OFFSET

  const open = assetOpen === assetId

  return (
    <>
      {hideServiceLines ? (
        <SvgWaterDrop ref={ref} />
      ) : (
        <svg
          height="100"
          ref={ref}
          style={svgStyle}
          viewBox="0 0 200 100"
          width="200">
          <rect fill="white" height="100" width="200" />
          <rect fill={publicMaterialColor} height="100" width="97" />
          <rect fill={privateMaterialColor} height="100" width="97" x="103" />
          <path
            d={publicMaterialIcon}
            fill="white"
            style={{
              transform: `scale(.1) translate(${publicIconOffset}px, 240px)`
            }}
          />
          <path
            d={privateMaterialIcon}
            fill="white"
            style={{
              transform: `scale(.1) translate(${privateIconOffset}px, 240px)`
            }}
          />
        </svg>
      )}

      <AppMarker
        icon={icon}
        {...rest}
        assetId={assetId}
        isLine
        locationId={locationId}
        open={open}
        privateMaterial={privateMaterial}
        privateMaterialColor={privateMaterialColor}
        privateVerifiedDate={privateVerifiedDate}
        publicMaterial={publicMaterial}
        publicMaterialColor={publicMaterialColor}
        publicVerifiedDate={publicVerifiedDate}
        setAssetOpen={setAssetOpen}
      />
    </>
  )
}

const svgStyle: CSSProperties = {
  borderRadius: '4px'
}

export default AppMarkerServiceLine
